import { useEffect, useMemo, useState } from 'react';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import { getFilters } from '@de/services/offeringsService';
import { resolveDynamicFilters } from '@de/utils/resolveDynamicFilters';
import type { Option } from '@de/types/types';

const USER_FACING_FETCH_ERROR = 'Failed to load options';

export type OfferingsFilterOptionsConfig = {
  type?: string;
  subject?: string;
  filterKey?: 'types' | 'subTypes' | 'subjects' | 'skills';
};

export interface UseOfferingsFilterOptionsReturn {
  dynamicOptions: Option[];
  isLoading: boolean;
  fetchError?: string;
}

/**
 * Maps an offerings `getFilters` response into choice `Option[]`.
 * Missing filters, missing `filterKey` data, and empty arrays all yield `[]`.
 */
export const mapFilterResponseToOptions = (
  filters: Record<string, unknown> | null | undefined,
  filterKey: string,
): Option[] => {
  if (!filters) {
    return [];
  }

  const filterData = filters[filterKey];
  if (!filterData) {
    return [];
  }

  let optionsList: string[] = [];

  if (Array.isArray(filterData)) {
    optionsList = filterData.filter(
      (value): value is string => typeof value === 'string',
    );
  } else if (typeof filterData === 'object') {
    optionsList = Object.entries(filterData as Record<string, number>)
      .sort(([aKey, aCount], [bKey, bCount]) => {
        if (bCount !== aCount) {
          return bCount - aCount;
        }
        return aKey.localeCompare(bKey);
      })
      .map(([key]) => key);
  }

  return optionsList.map((optionValue) => ({
    optionId: optionValue,
    text: optionValue,
    value: optionValue,
  }));
};

/**
 * Fetches offerings filter options for standalone dynamic single/multi choice
 * questions. Ignores stale in-flight responses when filter dependencies change
 * so a late failure cannot clobber a newer success.
 */
export const useOfferingsFilterOptions = (
  dynamicOptionsFilters?: OfferingsFilterOptionsConfig,
): UseOfferingsFilterOptionsReturn => {
  const { flowState } = useDynamicExperienceContext();
  const [dynamicOptions, setDynamicOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | undefined>();

  const resolvedFilters = useMemo(
    () => resolveDynamicFilters(dynamicOptionsFilters, flowState),
    [dynamicOptionsFilters, flowState],
  );

  const filterKey = dynamicOptionsFilters?.filterKey || 'subjects';
  const dependencyKey = `${resolvedFilters.type ?? ''}|${resolvedFilters.subject ?? ''}|${filterKey}`;

  useEffect(() => {
    let ignore = false;
    const requestFilters = {
      type: resolvedFilters.type,
      subject: resolvedFilters.subject,
    };

    const fetchOptions = async () => {
      setIsLoading(true);
      setFetchError(undefined);
      try {
        const filters = await getFilters(requestFilters);
        if (ignore) {
          return;
        }

        setDynamicOptions(
          mapFilterResponseToOptions(
            filters as Record<string, unknown> | null,
            filterKey,
          ),
        );
      } catch (error) {
        console.error('Error fetching filter options:', error);
        if (ignore) {
          return;
        }
        setFetchError(USER_FACING_FETCH_ERROR);
        setDynamicOptions([]);
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    void fetchOptions();

    return () => {
      ignore = true;
    };
    // dependencyKey encodes type/subject/filterKey; listing those primitives
    // keeps the effect aligned with the request payload without object-identity churn.
  }, [dependencyKey, resolvedFilters.type, resolvedFilters.subject, filterKey]);

  return { dynamicOptions, isLoading, fetchError };
};
